import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { 
  ArrowLeft, Award, Target, TrendingUp, DollarSign, Download, 
  Calendar, RefreshCw, Users, Building2, Trophy, Percent, CheckCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminIncentives = ({ user, onLogout }) => {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useState('');
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [performanceData, setPerformanceData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Generate month options for last 12 months
  const monthOptions = [];
  const currentDate = new Date();
  for (let i = 0; i < 12; i++) {
    const date = new Date(currentDate.getFullYear(), currentDate.getMonth() - i, 1);
    monthOptions.push({
      month: date.getMonth() + 1,
      year: date.getFullYear(),
      label: date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    });
  }

  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProperty) {
      fetchPerformanceData();
    }
  }, [selectedProperty, selectedMonth, selectedYear]);

  const fetchProperties = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/properties`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const activeProps = res.data.filter(p => p.status !== 'archived');
      setProperties(activeProps);
      if (activeProps.length > 0) {
        setSelectedProperty(activeProps[0].hotel_name);
      }
    } catch (error) {
      toast.error('Failed to load properties');
    }
  };

  const fetchPerformanceData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(
        `${API}/therapist-performance/monthly-stats/${encodeURIComponent(selectedProperty)}?month=${selectedMonth}&year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPerformanceData(res.data);
    } catch (error) {
      console.error('Error fetching performance:', error);
      setPerformanceData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleMonthChange = (value) => {
    const [month, year] = value.split('-').map(Number);
    setSelectedMonth(month);
    setSelectedYear(year);
  };

  const exportToExcel = () => {
    if (!performanceData) {
      toast.error('No data to export');
      return;
    }

    // Create CSV content
    const headers = [
      'Therapist Name', 'Services', 'Revenue', 'Contribution %', 'Incentive Amount'
    ];
    
    const rows = performanceData.therapists.map(t => [
      t.name,
      t.total_services,
      `₹${t.total_revenue.toLocaleString()}`,
      `${t.contribution_percent}%`,
      `₹${t.incentive_amount.toLocaleString()}`
    ]);
    
    // Add summary rows
    rows.push([]);
    rows.push(['Summary', '', '', '', '']);
    rows.push(['Property', performanceData.property_id, '', '', '']);
    rows.push(['Month', performanceData.month_name, '', '', '']);
    rows.push(['Monthly Target', `₹${performanceData.monthly_target.toLocaleString()}`, '', '', '']);
    rows.push(['Total Achieved', `₹${performanceData.total_achieved.toLocaleString()}`, '', '', '']);
    rows.push(['Achievement', `${performanceData.achievement_percent}%`, '', '', '']);
    rows.push(['Threshold', `₹${performanceData.threshold_amount.toLocaleString()} (${performanceData.threshold_percent}%)`, '', '', '']);
    rows.push(['Incentive Eligible', performanceData.incentive_eligible ? 'Yes' : 'No', '', '', '']);
    rows.push(['Total Incentive', `₹${performanceData.total_incentive.toLocaleString()}`, '', '', '']);
    rows.push(['Per Therapist', `₹${performanceData.per_therapist_incentive.toLocaleString()}`, '', '', '']);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.join(','))
    ].join('\n');
    
    // Download file
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `incentive_report_${selectedProperty}_${selectedMonth}_${selectedYear}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
    
    toast.success('Report exported successfully');
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="bg-white/80 backdrop-blur-sm border-b border-border/50 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <Button variant="outline" size="sm" data-testid="back-button">
                <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
              </Button>
            </Link>
            <h1 className="text-2xl font-serif text-foreground">Incentive Management</h1>
          </div>
          <Button 
            variant="outline" 
            onClick={exportToExcel}
            disabled={!performanceData}
            data-testid="export-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Export Report
          </Button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6 space-y-6">
        {/* Filters */}
        <div className="glass rounded-2xl p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Building2 className="w-4 h-4 text-muted-foreground" />
              <Select value={selectedProperty} onValueChange={setSelectedProperty}>
                <SelectTrigger className="w-[200px]" data-testid="property-select">
                  <SelectValue placeholder="Select Property" />
                </SelectTrigger>
                <SelectContent>
                  {properties.map(p => (
                    <SelectItem key={p.hotel_name} value={p.hotel_name}>
                      {p.hotel_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-muted-foreground" />
              <Select 
                value={`${selectedMonth}-${selectedYear}`} 
                onValueChange={handleMonthChange}
              >
                <SelectTrigger className="w-[180px]" data-testid="month-select">
                  <SelectValue placeholder="Select Month" />
                </SelectTrigger>
                <SelectContent>
                  {monthOptions.map(opt => (
                    <SelectItem key={`${opt.month}-${opt.year}`} value={`${opt.month}-${opt.year}`}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button variant="outline" size="sm" onClick={fetchPerformanceData}>
              <RefreshCw className="w-4 h-4 mr-1" />
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="glass rounded-2xl p-12 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto" />
            <p className="mt-4 text-muted-foreground">Loading incentive data...</p>
          </div>
        ) : !performanceData ? (
          <div className="glass rounded-2xl p-12 text-center">
            <Target className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <p className="text-muted-foreground">No target configured for this property</p>
            <p className="text-sm text-muted-foreground mt-1">
              Configure monthly target in Property Management
            </p>
          </div>
        ) : (
          <>
            {/* Summary Cards */}
            <div className="grid md:grid-cols-4 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Monthly Target</CardDescription>
                  <CardTitle className="text-2xl">
                    ₹{performanceData.monthly_target.toLocaleString()}
                  </CardTitle>
                </CardHeader>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Total Achieved</CardDescription>
                  <CardTitle className={`text-2xl ${performanceData.achievement_percent >= performanceData.threshold_percent ? 'text-green-600' : ''}`}>
                    ₹{performanceData.total_achieved.toLocaleString()}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{performanceData.achievement_percent}% of target</p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardDescription>Threshold ({performanceData.threshold_percent}%)</CardDescription>
                  <CardTitle className="text-2xl">
                    ₹{performanceData.threshold_amount.toLocaleString()}
                  </CardTitle>
                </CardHeader>
              </Card>
              
              <Card className={performanceData.incentive_eligible ? 'border-green-500 bg-green-50' : ''}>
                <CardHeader className="pb-2">
                  <CardDescription className="flex items-center gap-1">
                    Total Incentive
                    {performanceData.incentive_eligible && (
                      <Badge className="bg-green-500 text-white text-xs ml-2">Unlocked!</Badge>
                    )}
                  </CardDescription>
                  <CardTitle className={`text-2xl ${performanceData.incentive_eligible ? 'text-green-600' : ''}`}>
                    ₹{performanceData.total_incentive.toLocaleString()}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">
                    ₹{performanceData.per_therapist_incentive.toLocaleString()} per therapist
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Progress Bar */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Achievement Progress - {performanceData.month_name}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>₹0</span>
                    <span className="text-amber-600">{performanceData.threshold_percent}% threshold</span>
                    <span>₹{performanceData.monthly_target.toLocaleString()}</span>
                  </div>
                  <div className="relative">
                    <Progress 
                      value={Math.min(performanceData.achievement_percent, 100)} 
                      className="h-4"
                    />
                    {/* Threshold marker */}
                    <div 
                      className="absolute top-0 bottom-0 w-0.5 bg-amber-500"
                      style={{ left: `${performanceData.threshold_percent}%` }}
                    />
                  </div>
                  <p className="text-center text-lg font-semibold">
                    {performanceData.achievement_percent}% achieved
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Therapist Breakdown */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  Therapist Breakdown ({performanceData.active_therapist_count} active)
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {performanceData.therapists.length === 0 ? (
                    <p className="text-center text-muted-foreground py-4">No therapist data available</p>
                  ) : (
                    performanceData.therapists.map((therapist, index) => (
                      <div 
                        key={therapist.id || index} 
                        className="flex items-center justify-between p-4 bg-muted/30 rounded-lg"
                        data-testid={`therapist-row-${index}`}
                      >
                        <div className="flex items-center gap-3">
                          {index === 0 && therapist.total_revenue > 0 && (
                            <Trophy className="w-5 h-5 text-amber-500" />
                          )}
                          <div>
                            <p className="font-medium">{therapist.name}</p>
                            <p className="text-sm text-muted-foreground">
                              {therapist.total_services} services
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-6 text-right">
                          <div>
                            <p className="text-sm text-muted-foreground">Revenue</p>
                            <p className="font-medium">₹{therapist.total_revenue.toLocaleString()}</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Contribution</p>
                            <p className="font-medium">{therapist.contribution_percent}%</p>
                          </div>
                          <div>
                            <p className="text-sm text-muted-foreground">Incentive</p>
                            <p className={`font-medium ${therapist.incentive_amount > 0 ? 'text-green-600' : ''}`}>
                              ₹{therapist.incentive_amount.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Calculation Logic */}
            <Card>
              <CardHeader>
                <CardTitle>Incentive Calculation Logic</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-medium text-primary">1</span>
                    </div>
                    <p>
                      <strong>Threshold</strong> = {performanceData.threshold_percent}% of Monthly Target 
                      = ₹{performanceData.threshold_amount.toLocaleString()}
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-medium text-primary">2</span>
                    </div>
                    <p>
                      <strong>Excess Amount</strong> = Total Sales - Threshold 
                      = ₹{performanceData.total_achieved.toLocaleString()} - ₹{performanceData.threshold_amount.toLocaleString()} 
                      = <span className={performanceData.amount_above_threshold > 0 ? 'text-green-600' : ''}>
                        ₹{performanceData.amount_above_threshold.toLocaleString()}
                      </span>
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-medium text-primary">3</span>
                    </div>
                    <p>
                      <strong>Total Incentive</strong> = {performanceData.incentive_percentage}% of Excess Amount 
                      = {performanceData.incentive_percentage}% × ₹{performanceData.amount_above_threshold.toLocaleString()} 
                      = <span className={performanceData.total_incentive > 0 ? 'text-green-600' : ''}>
                        ₹{performanceData.total_incentive.toLocaleString()}
                      </span>
                    </p>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <span className="text-xs font-medium text-primary">4</span>
                    </div>
                    <p>
                      <strong>Per Therapist</strong> = Total Incentive ÷ Active Therapists 
                      = ₹{performanceData.total_incentive.toLocaleString()} ÷ {performanceData.active_therapist_count || 1} 
                      = <span className={performanceData.per_therapist_incentive > 0 ? 'text-green-600' : ''}>
                        ₹{performanceData.per_therapist_incentive.toLocaleString()}
                      </span>
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </div>
  );
};

export default AdminIncentives;
